import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import CryptoJS from 'crypto-js';
import Cookies from 'js-cookie';

const SECRET_KEY = 'tabtrips_hotel_secret';
const TOKEN_COOKIE_KEY = 'token';
const FETCH_COOLDOWN_MS = 10000;

/** Module-level cache so every useHotelList() share one in-flight request. */
let sharedHotels = [];
let sharedLastFetch = 0;
let sharedPromise = null;
let bootstrapped = false;
const listeners = new Set();

function encryptData(data) {
  return CryptoJS.AES.encrypt(JSON.stringify(data), SECRET_KEY).toString();
}

function getDecryptedToken() {
  const encrypted = Cookies.get(TOKEN_COOKIE_KEY);
  if (!encrypted) return null;
  try {
    const bytes = CryptoJS.AES.decrypt(encrypted, SECRET_KEY);
    return bytes.toString(CryptoJS.enc.Utf8);
  } catch {
    return null;
  }
}

function notifyListeners(nextHotels) {
  listeners.forEach((fn) => {
    try {
      fn(nextHotels);
    } catch {
      /* ignore subscriber errors */
    }
  });
}

async function fetchHotelsShared(force = false) {
  const now = Date.now();
  if (!force && sharedPromise) {
    return sharedPromise;
  }
  if (!force && sharedHotels.length > 0 && now - sharedLastFetch < FETCH_COOLDOWN_MS) {
    return sharedHotels;
  }

  const token = getDecryptedToken();
  if (!token) {
    return sharedHotels;
  }

  sharedPromise = (async () => {
    const formData = new FormData();
    formData.append('token', token);

    const response = await axios.post(
      'https://api.tabtrips.com/olivrweb/hotelapi/dashboardApi.php/getHostAllHotels',
      formData
    );

    if (!response.data?.status) {
      throw new Error(response.data?.message || 'Failed to fetch hotels');
    }

    const hotelsData = response.data.data || [];
    const allSubHosts = response.data.allSubHosts || [];

    sharedHotels = hotelsData;
    sharedLastFetch = Date.now();

    localStorage.setItem('hotelsList', encryptData(hotelsData));
    if (allSubHosts.length > 0) {
      localStorage.setItem('allSubHosts', encryptData(allSubHosts));
    }

    if (hotelsData.length > 0) {
      const currentHotelId = localStorage.getItem('selectedHotelId');
      const currentHostId = localStorage.getItem('selectedHostId');

      if (!currentHotelId) {
        const firstHotel = hotelsData[0];
        localStorage.setItem('selectedHotelId', firstHotel.hotel_id);
        localStorage.setItem('selectedHotelName', firstHotel.hotel_name);
        if (firstHotel.host_id && !currentHostId) {
          localStorage.setItem('selectedHostId', firstHotel.host_id);
        }
      } else {
        const matchingHotel = hotelsData.find((h) => String(h.hotel_id) === String(currentHotelId));
        if (!matchingHotel) {
          const firstHotel = hotelsData[0];
          localStorage.setItem('selectedHotelId', firstHotel.hotel_id);
          localStorage.setItem('selectedHotelName', firstHotel.hotel_name);
          if (firstHotel.host_id) {
            localStorage.setItem('selectedHostId', firstHotel.host_id);
          }
        } else {
          const currentHotelName = localStorage.getItem('selectedHotelName');
          if (currentHotelName !== matchingHotel.hotel_name) {
            localStorage.setItem('selectedHotelName', matchingHotel.hotel_name);
          }
          if (matchingHotel.host_id && matchingHotel.host_id !== currentHostId) {
            localStorage.setItem('selectedHostId', matchingHotel.host_id);
          }
        }
      }
    } else {
      localStorage.removeItem('selectedHotelId');
      localStorage.removeItem('selectedHotelName');
      localStorage.removeItem('selectedHostId');
    }

    notifyListeners(hotelsData);
    window.dispatchEvent(new Event('hotelsUpdated'));
    return hotelsData;
  })();

  try {
    return await sharedPromise;
  } finally {
    sharedPromise = null;
  }
}

/** Clear module cache (logout / pre-login). */
export function resetHotelListCache() {
  sharedHotels = [];
  sharedLastFetch = 0;
  sharedPromise = null;
  bootstrapped = false;
  notifyListeners([]);
}

/** Force getHostAllHotels after token is stored (login / signup). */
export async function prefetchHotelsAfterAuth() {
  bootstrapped = true;
  return fetchHotelsShared(true);
}

const useHotelList = () => {
  const [hotels, setHotels] = useState(() => sharedHotels);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const onUpdate = (next) => setHotels(Array.isArray(next) ? next : []);
    listeners.add(onUpdate);
    if (sharedHotels.length > 0) {
      setHotels(sharedHotels);
    }
    return () => listeners.delete(onUpdate);
  }, []);

  const fetchHotels = useCallback(async (force = false) => {
    try {
      setLoading(true);
      setError(null);
      const hotelsData = await fetchHotelsShared(force);
      setHotels(hotelsData || []);
      return hotelsData;
    } catch (err) {
      console.error('Error fetching hotel list:', err);
      setError(err.message || 'An error occurred');
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!bootstrapped) {
      bootstrapped = true;
      const pageLoadTime = sessionStorage.getItem('pageLoadTime');
      const currentTime = Date.now().toString();
      const isActualPageLoad = !pageLoadTime;
      sessionStorage.setItem('pageLoadTime', currentTime);
      fetchHotels(isActualPageLoad);
    } else if (sharedHotels.length > 0) {
      setHotels(sharedHotels);
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const lastVisible = parseInt(sessionStorage.getItem('lastVisible') || '0', 10);
        const now = Date.now();
        if (now - lastVisible > 30000) {
          sessionStorage.setItem('lastVisible', String(now));
          fetchHotels(false);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [fetchHotels]);

  return { hotels, loading, error, refetch: fetchHotels };
};

export default useHotelList;
